import React from "react";
import cx from "clsx";
import styles from "@scss/custom-components/ai-robot.module.scss";

import ArmLeft from "@images/ai-robot/arm-l.svg";
import ArmRight from "@images/ai-robot/arm-r.svg";
import Body from "@images/ai-robot/body.svg";
import BodyError from "@images/ai-robot/body-error.svg";
import Eyes from "@images/ai-robot/eyes.svg";
import EyesSmile from "@images/ai-robot/eyes-smile.svg";
import EyesThink from "@images/ai-robot/eyes-think.svg";
import EyesError from "@images/ai-robot/eyes-error.svg";

export interface AiRobotProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "default" | "smile" | "think" | "error";
  hover?: boolean;
  scale?: number;
}

export const CustomAiRobot: React.FC<AiRobotProps> = ({
  variant = "default",
  hover = false,
  scale = 1,
  className,
  ...divProps
}) => {
  const bodySrc = variant === "error" ? BodyError : Body;

  const bodyClass = cx(styles.aiRobotBody, styles[`aiRobotBody--${variant}`]);

  const armLeftClass = cx(
    styles.aiRobotArmLeft,
    styles[`aiRobotArmLeft--${variant}`],
  );

  const armRightClass = cx(
    styles.aiRobotArmRight,
    styles[`aiRobotArmRight--${variant}`],
  );

  const eyesDefaultClass = cx(
    styles.aiEyesDefault,
    (variant === "default" || hover) && styles.aiEyesVisible,
    variant === "smile" && styles.aiEyesHidden,
    variant === "think" && styles.aiEyesHidden,
    variant === "error" && styles.aiEyesHidden,
  );

  const eyesSmileClass = cx(
    styles.aiEyesSmile,
    variant === "default" && styles.aiEyesHidden,
    variant === "smile" && styles.aiEyesVisible,
    variant === "think" && styles.aiEyesHidden,
    variant === "error" && styles.aiEyesHidden,
    hover && styles.aiEyesVisibleHover,
  );

  const eyesThinkClass = cx(
    styles.aiEyesThink,
    variant === "default" && styles.aiEyesHidden,
    variant === "smile" && styles.aiEyesHidden,
    variant === "think" && styles.aiEyesVisible,
    variant === "error" && styles.aiEyesHidden,
  );

  const eyesErrorClass = cx(
    styles.aiEyesError,
    variant === "default" && styles.aiEyesHidden,
    variant === "smile" && styles.aiEyesHidden,
    variant === "think" && styles.aiEyesHidden,
    variant === "error" && styles.aiEyesVisible,
  );

  return (
    <div
      className={cx(
        styles.aiRobot,
        styles[`aiRobot--${variant}`],
        { [styles["aiRobot--hover"]]: hover },
        className,
      )}
      style={{ "--robot-scale": scale } as React.CSSProperties}
      {...divProps}
    >
      <div className={styles.aiAntennaGlow} />

      <img src={ArmLeft} alt="Left Arm" className={armLeftClass} />
      <img src={ArmRight} alt="Right Arm" className={armRightClass} />
      <img
        src={bodySrc}
        alt={variant === "error" ? "Robot body (error)" : "Robot body"}
        className={bodyClass}
      />
      <img src={Eyes} alt="Default eyes" className={eyesDefaultClass} />
      <img src={EyesSmile} alt="Smiling eyes" className={eyesSmileClass} />
      <img src={EyesThink} alt="Thinking eyes" className={eyesThinkClass} />
      <img src={EyesError} alt="Error eyes" className={eyesErrorClass} />
    </div>
  );
};
